<template>
  <div class="position-relative h-100 w-100">
    <div class="map-header position-absolute text-light">
      <h2 class="h3 m-0"><span>Day 02 - </span>Lines</h2>
    </div>
    <div ref="map-root" class="basemap"></div>
  </div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import "mapbox-gl/src/css/mapbox-gl.css";
import turf from "turf";
import line from "../assets/line.geojson";

export default {
  data() {
    return {
      accessToken:
        "pk.eyJ1Ijoia21hcnNoYWxsIiwiYSI6ImNrdXdudGlpODI2ejkydXF2bXcyM3poaWQifQ.hyBCr6uwWlDGMGyVmuKCIw",
      geojson: line,
    };
  },
  methods: {
    initMap() {
      mapboxgl.accessToken = this.accessToken;

      (async () => {
        const map = new mapboxgl.Map({
          container: this.$refs["map-root"],
          zoom: 13,
          center: [-5.168568, 55.584649],
          pitch: 76,
          bearing: -100,
          style: "mapbox://styles/mapbox/satellite-streets-v11",
          interactive: false,
          hash: false,
        });

        // Start downloading the route data, and wait for map load to occur in parallel
        const [pinRouteGeojson] = await Promise.all([
          this.geojson,
          map.once("load"),
        ]);

        const pinRoute = pinRouteGeojson.features[0].geometry.coordinates;
        // Create the marker and popup that will display the elevation queries
        const popup = new mapboxgl.Popup({ closeButton: false });
        const marker = new mapboxgl.Marker({
          color: "red",
          scale: 0.8,
          draggable: false,
          pitchAlignment: "auto",
          rotationAlignment: "auto",
        })
          .setLngLat(pinRoute[0])
          .setPopup(popup)
          .addTo(map)
          .togglePopup();

        // Add a line feature and layer. This feature will get updated as we progress the animation
        map.addSource("line", {
          type: "geojson",
          // Line metrics is required to use the 'line-progress' property
          lineMetrics: true,
          data: pinRouteGeojson,
        });
        map.addLayer({
          type: "line",
          source: "line",
          id: "line",
          paint: {
            "line-color": "rgba(0,0,0,0)",
            "line-width": 10,
          },
          layout: {
            "line-cap": "round",
            "line-join": "round",
          },
        });

        // Add some fog in the background
        map.setFog({
          range: [-0.5, 8],
          color: "white",
          "horizon-blend": 0.2,
        });

        // Add a sky layer over the horizon
        map.addLayer({
          id: "sky",
          type: "sky",
          paint: {
            "sky-type": "atmosphere",
            "sky-atmosphere-color": "rgba(85, 151, 210, 0.5)",
          },
        });

        // Add terrain source, with slight exaggeration
        map.addSource("mapbox-dem", {
          type: "raster-dem",
          url: "mapbox://mapbox.terrain-rgb",
          tileSize: 512,
          maxzoom: 14,
        });
        map.setTerrain({ source: "mapbox-dem", exaggeration: 1.5 });

        await map.once("idle");
        // The total animation duration, in milliseconds
        const animationDuration = 20000;
        // Use the https://turfjs.org/ library to calculate line distances and
        // sample the line at a given percentage with the turf.along function.
        const path = turf.lineString(pinRoute);
        // Get the total line distance
        const pathDistance = turf.lineDistance(path);
        let start;
        function frame(time) {
          if (!start) start = time;
          const animationPhase = (time - start) / animationDuration;
          if (animationPhase > 1) {
            return;
          }

          // Get the new latitude and longitude by sampling along the path
          const alongPath = turf.along(path, pathDistance * animationPhase)
            .geometry.coordinates;
          const lngLat = {
            lng: alongPath[0],
            lat: alongPath[1],
          };

          // Sample the terrain elevation. We round to an integer value to
          // prevent showing a lot of digits during the animation
          const elevation = Math.floor(
            // Do not use terrain exaggeration to get actual meter values
            map.queryTerrainElevation(lngLat, { exaggerated: false })
          );

          const ft = Math.round(elevation * 3.2808);

          // Update the popup altitude value and marker location
          popup.setHTML("Altitude: " + ft + " ft<br/>");
          marker.setLngLat(lngLat);

          // Reduce the visible length of the line by using a line-gradient to cutoff the line
          // animationPhase is a value between 0 and 1 that reprents the progress of the animation
          map.setPaintProperty("line", "line-gradient", [
            "step",
            ["line-progress"],
            "red",
            animationPhase,
            "rgba(255, 0, 0, 0)",
          ]);

          // Rotate the camera at a slightly lower speed to give some parallax effect in the background
          const rotation = -100 - animationPhase * 30.0;
          map.setBearing(rotation % 360);

          window.requestAnimationFrame(frame);
        }

        window.requestAnimationFrame(frame);
      })();
    },
  },
  mounted() {
    this.initMap();
  },
};
</script>

<style lang="scss" scoped>
@import "../assets/scss/map-shared.scss";
:deep(.mapboxgl-popup-content) {
  color: #191919;
  z-index: 3;
  font-size: 1rem;
  font-family: inherit;
}
</style>
